import Avatar from '@/components/base/Avatar';
import type { BulkRecipient } from '@/types/portal';

interface RecipientRosterProps {
  rows: BulkRecipient[];
  currency: string;
  onWalletId: (id: string, value: string) => void;
  onAmount: (id: string, amount: number) => void;
  onDescription: (id: string, value: string) => void;
  onRemove: (id: string) => void;
}

export default function RecipientRoster({
  rows,
  currency,
  onWalletId,
  onAmount,
  onDescription,
  onRemove,
}: RecipientRosterProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-background-200">
      <table className="row-accent w-full min-w-[880px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 bg-background-100 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Wallet ID / Number</th>
            <th className="px-4 py-3">Amount ({currency})</th>
            <th className="px-4 py-3">Description</th>
            <th className="w-12 px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const ledger = row.walletType === 'Ledger';
            return (
              <tr key={row.id} className="border-b border-background-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    {row.avatar ? (
                      <Avatar src={row.avatar} alt={row.name} size="sm" />
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-xs font-medium text-foreground-600">
                        {row.name.slice(0, 1)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-950">{row.name}</p>
                      <p className="text-xs text-foreground-500">{row.market}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {row.walletType === 'Mobile' ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-smartphone-line text-foreground-400" />
                        {row.network}
                      </span>
                    ) : (
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-label font-medium ${
                          ledger
                            ? 'border-accent-200 bg-accent-100 text-accent-800'
                            : 'border-secondary-200 bg-secondary-100 text-secondary-900'
                        }`}
                      >
                        {ledger ? 'Ledger' : 'Bank'}
                      </span>
                    )}
                    <input
                      value={row.walletId}
                      onChange={(event) => onWalletId(row.id, event.target.value)}
                      placeholder={row.walletType === 'Mobile' ? 'e.g. 254712345678' : 'e.g. 2314'}
                      aria-label={row.walletType === 'Mobile' ? `${row.name} mobile number` : `${row.name} wallet ID`}
                      className="h-9 w-44 rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  {row.status === 'Blocked' ? (
                    <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary-700">
                      <i className="ri-error-warning-line" />
                      {row.issue}
                    </p>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    min={0}
                    value={row.amount}
                    onChange={(event) => onAmount(row.id, Number(event.target.value))}
                    className="h-9 w-32 rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    value={row.description}
                    onChange={(event) => onDescription(row.id, event.target.value)}
                    maxLength={80}
                    className="h-9 w-full min-w-[160px] rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(row.id)}
                    aria-label={`Remove ${row.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-primary-200 text-primary-700 cursor-pointer transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-close-line" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}